import { AlertCircle } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@bookdental/ui";
import type { DbField } from "@bookdental/schemas";

type Props = {
  headers: string[];
  sampleRow: Record<string, string> | undefined;
  dbFields: DbField[];
  mappings: Record<string, string>;
  onChange: (col: string, value: string) => void;
  requiredField: string;
  requiredLabel: string;
  totalRows: number;
};

export function MappingTable({
  headers,
  sampleRow,
  dbFields,
  mappings,
  onChange,
  requiredField,
  requiredLabel,
  totalRows,
}: Props) {
  const usedFields = new Set(Object.values(mappings).filter((v) => v && v !== "ignore"));
  const hasRequired = usedFields.has(requiredField);

  return (
    <div className="space-y-3">
      <p className="text-xs text-zinc-600">
        <strong>{totalRows}</strong> registros encontrados. Associe cada coluna do arquivo a um
        campo do sistema:
      </p>

      {!hasRequired && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
          <p className="text-xs text-amber-800">
            O campo <strong>{requiredLabel}</strong> e obrigatorio. Mapeie ao menos uma coluna.
          </p>
        </div>
      )}

      <div className="max-h-[360px] overflow-auto rounded-lg border border-zinc-200">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-zinc-50 text-zinc-600">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Coluna do arquivo</th>
              <th className="px-3 py-2 text-left font-semibold">Campo no sistema</th>
              <th className="px-3 py-2 text-left font-semibold">Amostra</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 bg-white">
            {headers.map((col) => {
              const sample = sampleRow?.[col] ?? "";
              const current = mappings[col] ?? "";
              return (
                <tr key={col}>
                  <td className="px-3 py-2 align-top font-medium text-zinc-800">{col}</td>
                  <td className="px-3 py-2 align-top">
                    <Select
                      value={current || "ignore"}
                      onValueChange={(v) => onChange(col, v === "ignore" ? "" : v)}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="-- Ignorar --" />
                      </SelectTrigger>
                      <SelectContent className="z-[60] max-h-[280px] border-zinc-200 bg-white shadow-xl">
                        <SelectItem value="ignore">-- Ignorar --</SelectItem>
                        {dbFields.map((f) => {
                          const disabled = usedFields.has(f.key) && current !== f.key;
                          return (
                            <SelectItem key={f.key} value={f.key} disabled={disabled}>
                              {f.label}
                              {f.key === requiredField ? " *" : ""}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2 align-top text-[11px] text-zinc-500">
                    <span className="block max-w-[240px] truncate">{sample.slice(0, 60)}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
